import { useMemo, useEffect, useState, createRef } from 'react'
import html2canvas from 'html2canvas'

import { TDynamicKey } from '../../../utils/type'
import { menuInfo } from '../../LayoutPreview'
import { TableCompareAssessmentProps, TSections, DataSource } from '../../TableCompareAssessment'
import { thaiWordWrap } from '../../../utils/wordcut/thaiWordWrap'
import useExportReportCompareExcel from './useExportReportCompareExcel'
import useExportReportComparePDF from './useExportReportComparePDF'
import useStore from '../../../stores/useStore'

export enum ExportType {
    BROWSER_PDF = 'open:pdf',
    BROWSER_EXCEL = 'open:excel',
    DOWNLOAD_PDF = 'download:pdf',
    DOWNLOAD_EXCEL = 'download:excel',
    DOWNLOAD_WORD = 'download:word',
}

export type TDataSpiderChart = {
    title: string
    labels: string[]
    user: { data: number[] }
    admin: { data: number[] }
    color?: string[]
    key?: string
}

type TUseTableCompareAssessmentReport = {
    questionnaireScore?: TDynamicKey
    questionnaireName?: string
    maxColTable?: number
}

export type THeaderExcel = {
    key: string
    name: string
    width: number
    cell: number
}

export type TContentExcel = string | number | null

export type TListSpiderChartImage = {
    image: string
    title: string
}

type TDataRowTablePDF =
    | {
          text: string
          fillColor?: string
          alignment?: string
          style?: string
          margin?: Array<number>
      }
    | string

type TDataRowMatrixTablePDF = number | null | undefined

let useTableCompareAssessmentReport = (props: TableCompareAssessmentProps & TUseTableCompareAssessmentReport) => {
    const { maxColTable = 5 } = props
    const { lookupStore } = useStore()
    const { downloadExcel } = useExportReportCompareExcel()
    const { downloadPdf, openPdf } = useExportReportComparePDF({})
    const [refSpiderChart, setRefSpiderChart] = useState([])

    const columnsConfigLength = Object.keys(props.columnsConfig).length

    const color = useMemo(() => {
        if (lookupStore.colorChart.length && props.sections.length) {
            return props.dataSource.map((dataSource: DataSource, index: number) => lookupStore.colorChart[index].color)
        } else {
            return []
        }
    }, [lookupStore.colorChart, props.dataSource, props.sections.length])

    const dataSpiderChart: TDataSpiderChart[] = useMemo(() => {
        const labels = props.dataSource.map((data: DataSource) => `ด้านที่ ${data.rowKey}`)
        return Object.keys(props.columnsConfig).map((key: string) => ({
            key: key,
            title: `กราฟแสดงภาพรวม${props.columnsConfig[key]}`,
            labels: labels,
            color: color,
            user: { data: props.dataSource.map((data: DataSource) => data[key].user) },
            admin: { data: props.dataSource.map((data: DataSource) => data[key].admin) },
        }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.columnsConfig, props.dataSource])

    useEffect(() => {
        //สร้าง ref เพื่อไป generate รูปสำหรับออกรายงาน
        setRefSpiderChart((refSpiderChart) =>
            Array(columnsConfigLength)
                .fill('')
                .map((_, i) => refSpiderChart[i] || createRef()),
        )
    }, [columnsConfigLength])

    //colorChart
    useEffect(() => {
        lookupStore.getLookupColorChart()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const createHeaderExcel = (columns: TDynamicKey) => {
        const header: THeaderExcel[] = [
            {
                key: '-1',
                name: 'ด้าน',
                width: 60,
                cell: 1,
            },
        ]

        Object.keys(columns).forEach((key: string) => {
            header.push({
                key: key,
                //@ts-ignore
                name: columns[key],
                width: 60,
                cell: 2,
            })
        })

        return header
    }

    const getAVGDataByConfig = () => {
        const avgData: TDynamicKey = {}

        Object.keys(props.columnsConfig).forEach((key) => {
            const scoreUser = props.dataSource.map((item) => item[key].user).filter((score) => score || score === 0)
            const scoreAdmin = props.dataSource.map((item) => item[key].admin).filter((score) => score || score === 0)
            avgData[key] = {
                user: scoreUser.length ? scoreUser.reduce((total, cur) => total + cur) / scoreUser.length : undefined,
                admin: scoreAdmin.length
                    ? scoreAdmin.reduce((total, cur) => total + cur) / scoreAdmin.length
                    : undefined,
                // @ts-ignore
                score: props.questionnaireScore[key],
            }
        })

        return avgData
    }

    const convertAVGDataByConfigToArray = (avgData = {}) => {
        const result: any = []
        Object.keys(avgData).forEach((key) => {
            //@ts-ignore
            result.push(avgData[key].admin)
            //@ts-ignore
            result.push(avgData[key].user)
        })
        return result
    }

    const convertAVGDataByConfigToPercentArray = (avgData = {}) => {
        const result: any = []
        Object.keys(avgData).forEach((key) => {
            //@ts-ignore
            const percentAVGAdmin = avgData[key].admin
                ? //@ts-ignore
                  avgData[key].admin / avgData[key].score
                : //@ts-ignore
                  avgData[key].admin
            //@ts-ignore
            const percentAVGUser = avgData[key].user
                ? //@ts-ignore
                  avgData[key].user / avgData[key].score
                : //@ts-ignore
                  avgData[key].user

            result.push(percentAVGAdmin)
            result.push(percentAVGUser)
        })
        return result
    }

    const createContentExcel = (dataSource: DataSource[], columnConfig: TDynamicKey = {}, sections: TSections[]) => {
        const content: Array<Array<TContentExcel>> = [['']]
        const columnConfigKey = Object.keys(columnConfig)

        // row 1
        for (let i = 1; i <= columnConfigKey.length; i++) {
            content[0].push('โดยเจ้าหน้าที่')
            content[0].push('โดยตนเอง')
        }
        // row แต่ละด้าน
        dataSource.forEach((data: DataSource, index: number) => {
            if (index < sections.length) {
                const dataRow: Array<TContentExcel> = [`ด้านที่ ${index + 1} ${data.name}`]
                let times = 1

                while (times <= columnConfigKey.length && data[times]) {
                    dataRow.push(data[times].admin)
                    dataRow.push(data[times].user)
                    times++
                }

                content.push(dataRow)
            }
        })

        // row คะแนนเฉลี่ย
        const avgDataByConfigToArray = getAVGDataByConfig()
        const avg = convertAVGDataByConfigToArray(avgDataByConfigToArray)
        const dataRowAVG = ['คะแนนเฉลี่ย', ...avg] // รอเอาค่าที่มาจากการคำนวณ
        content.push(dataRowAVG)

        // row เปอร์เซนต์คะแนนเฉลี่ย
        const percent = convertAVGDataByConfigToPercentArray(avgDataByConfigToArray) // รอมาแก้
        const dataRowPercent = ['เปอร์เซ็นต์คะแนนเฉลี่ย', ...percent] // รอเอาค่าที่มาจากการคำนวณ
        content.push(dataRowPercent)

        return content
    }

    const getSpiderChartImage = async () => {
        const spiderChartImagePms = refSpiderChart.map(async (ref) => {
            //@ts-ignore
            const image = await html2canvas(ref.current, {
                scrollY: window.pageYOffset * -1,
                scale: 2,
                allowTaint: true,
                // width: 1080,
                // height: 1080,
            })
            return image.toDataURL('image/png', 1.0)
        })
        const spiderChartImage = await Promise.all(spiderChartImagePms)
        return spiderChartImage
    }

    const getListSpiderChartImage = async () => {
        const spiderChartImage = await getSpiderChartImage()
        const listSpiderChartImage: TListSpiderChartImage[] = spiderChartImage.map((image: string, index: number) => ({
            image: image,
            title: dataSpiderChart[index].title.replace(/\n/g, ''),
        }))
        return listSpiderChartImage
    }

    const createParamGenerateExcel = async () => {
        const header: THeaderExcel[] = createHeaderExcel(props.columnsConfig)
        const content: Array<Array<TContentExcel>> = createContentExcel(
            props.dataSource,
            props.columnsConfig,
            props.sections,
        )
        const footer: TListSpiderChartImage[] = await getListSpiderChartImage()

        return {
            title: props.questionnaireName,
            header,
            content,
            footer,
        }
    }

    const handleDownloadExcel = async () => {
        const paramGenerateExcel = await createParamGenerateExcel()
        downloadExcel(paramGenerateExcel)
    }

    const createDataHeaderTablePDF = (columnConfig: TDynamicKey) => {
        const templateHeader = {
            text: '',
            style: 'tableHeader',
            colSpan: 1,
            alignment: 'center',
            fillColor: '#093018',
            color: '#FFFFFF',
            bold: true,
            fontSize: 14,
        }
        const header1: Array<TDynamicKey | string> = [{ ...templateHeader, text: 'ด้าน' }] //header แถว 1
        const header2: Array<TDynamicKey | string> = [''] //header แถว 2

        // const warpTextWidth = Math.round(405 / maxColTable)
        Object.keys(columnConfig).forEach((key: string) => {
            let arrHeader: string[] = columnConfig[key]
                .replace(/\n/g, '  ')
                .split(/  +/g)
                .map((line: string) => thaiWordWrap(line.trim(), 35, '\n'))
            let textHeader: string = arrHeader.join('\n')

            header1.push({
                ...templateHeader,
                text: textHeader, // Header column ด้าน
                colSpan: 2,
                noWrap: false,
            })
            header1.push({})
            header2.push({ text: 'โดยเจ้าหน้าที่', fillColor: '#F0F9F8', alignment: 'center' })
            header2.push({ text: 'โดยตนเอง', fillColor: '#FEFCF2', alignment: 'center' })
        })
        return [header1, header2]
    }

    const createDataTablePDF = (
        dataSource: DataSource[],
        columnConfig: TDynamicKey,
        sections: TSections[],
        type?: string,
    ) => {
        let dataTable: Array<Array<TDataRowTablePDF> | Array<TDynamicKey | string>> = []
        const dataMatrix: Array<Array<TDataRowMatrixTablePDF>> = [] // เอาไว้สำหรับหาค่าเฉลี่ย
        // header
        const dataHeader: Array<Array<TDynamicKey | string>> = createDataHeaderTablePDF(columnConfig)
        dataTable = [...dataTable, ...dataHeader]

        const columnConfigKey = Object.keys(columnConfig)
        const margin_table_pdf = [10, 5, 10, 5]

        // row แต่ละด้าน
        dataSource.forEach((data: DataSource, index: number) => {
            if (index < sections.length) {
                const dataRow: TDataRowTablePDF[] = [
                    {
                        text: thaiWordWrap(`ด้านที่ ${index + 1} ${data.name}`.trim(), 30, '\n'), // ชื่อด้าน column ด้านซ้ายสุด
                        margin: [10, 5, 0, 5],
                        //@ts-ignore
                    },
                ]
                const dataRowMatrix: Array<TDataRowMatrixTablePDF> = []
                let dimension = 1

                while (dimension <= columnConfigKey.length && data[dimension]) {
                    dataRow.push({
                        text: data[dimension].admin?.toFixed(2) ?? '-',
                        fillColor: '#F0F9F8',
                        alignment: 'center',
                        margin: margin_table_pdf,
                    })
                    dataRow.push({
                        text: data[dimension].user?.toFixed(2) ?? '-',
                        fillColor: '#FEFCF2',
                        alignment: 'center',
                        margin: margin_table_pdf,
                    })

                    dataRowMatrix.push(data[dimension].admin)
                    dataRowMatrix.push(data[dimension].user)

                    dimension++
                }

                dataTable.push(dataRow)
                dataMatrix.push(dataRowMatrix)
            }
        })

        // row คะแนนเฉลี่ย
        const avgDataByConfigToArray = getAVGDataByConfig()
        const avg = convertAVGDataByConfigToArray(avgDataByConfigToArray)
        const dataRowAVG = [
            {
                text: 'คะแนนเฉลี่ย',
                style: 'textBold',
                margin: margin_table_pdf,
            },
            ...avg.map((avg: null | number, index: number) => ({
                text: avg ? avg.toFixed(2) : '-',
                style: 'textBold',
                alignment: 'center',
                fillColor: index % 2 === 0 ? '#F0F9F8' : '#FEFCF2',
                margin: margin_table_pdf,
            })),
        ]
        dataTable.push(dataRowAVG)

        // row เปอร์เซนต์คะแนนเฉลี่ย
        const percent = convertAVGDataByConfigToPercentArray(avgDataByConfigToArray)
        const dataRowPercent = [
            {
                text: 'เปอร์เซ็นต์คะแนนเฉลี่ย',
                style: 'textBold',
                margin: margin_table_pdf,
            },
            ...percent.map((percent: null | number, index: number) => ({
                text: percent || percent === 0 ? `${(percent * 100).toFixed(2)}%` : '-',
                alignment: 'center',
                style: 'textBold',
                fillColor: index % 2 === 0 ? '#F0F9F8' : '#FEFCF2',
                margin: margin_table_pdf,
            })),
        ]
        dataTable.push(dataRowPercent)

        return dataTable
    }

    const createParamGeneratePDF = async (type?: string) => {
        return {
            title: props.questionnaireName,
            dataTable: createDataTablePDF(props.dataSource, props.columnsConfig, props.sections, type),
            dataImage: await getListSpiderChartImage(),
        }
    }

    const handleDownloadPDF = async () => {
        const paramGeneratePDF = await createParamGeneratePDF()
        downloadPdf({ data: [paramGeneratePDF] }, maxColTable)
    }

    const handleOpenPDF = async () => {
        const paramGeneratePDF = await createParamGeneratePDF()
        openPdf({ data: [paramGeneratePDF] }, maxColTable)
    }

    const onClickMenu = (value: menuInfo) => {
        switch (value.key) {
            case ExportType.DOWNLOAD_EXCEL:
                handleDownloadExcel()
                break

            case ExportType.DOWNLOAD_PDF:
                handleDownloadPDF()
                break

            case ExportType.BROWSER_PDF:
                handleOpenPDF()
                break

            default:
                break
        }
    }

    return {
        refSpiderChart,
        dataSpiderChart,
        onClickMenu,
        createParamGeneratePDF,
        createParamGenerateExcel,
    }
}

export default useTableCompareAssessmentReport
